import React, { useCallback, useEffect, useState } from 'react';
import { TextField, FormHelperText, Box, Typography } from '@mui/material';
import { FIELD_STYLES } from './fieldStyles';

const CustomInputField = ({
  id,
  value = '',
  onChange,
  label,
  required = false,
  placeholder,
  maxLength,
  multiline = false,
  rows = 3,
  error,
  helperText,
  disabled = false,
  ...rest
}) => {
  const [charCount, setCharCount] = useState(value?.length ?? 0);

  useEffect(() => {
    setCharCount(value?.length ?? 0);
  }, [value]);

  const handleChange = useCallback(
    (e) => {
      const raw = maxLength
        ? e.target.value.slice(0, maxLength)
        : e.target.value;
      setCharCount(raw.length);
      if (onChange) onChange(id, raw);
    },
    [id, maxLength, onChange],
  );

  const nearLimit = maxLength && charCount >= maxLength - 15;
  const atLimit = maxLength && charCount >= maxLength;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {/* Label */}
      {label && (
        <Typography variant="body2" fontWeight={600}>
          {label}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Box>
          )}
        </Typography>
      )}

      {/* Input */}
      <TextField
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        fullWidth
        multiline={multiline}
        rows={multiline ? rows : undefined}
        variant="outlined"
        disabled={disabled}
        error={!!error}
        helperText={undefined}
        inputProps={{
          maxLength,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: FIELD_STYLES.minHeight,
          },

          // MuiOutlinedInput-input covers both <input> and <textarea> in MUI v5
          '& .MuiOutlinedInput-input': {
            fontSize: FIELD_STYLES.fontSize,
            lineHeight: FIELD_STYLES.lineHeight,
            padding: `${FIELD_STYLES.paddingY} ${FIELD_STYLES.paddingX}`,
          },

          // 🔥 only real fix needed
          '& .MuiInputBase-multiline': {
            padding: 0,
          },
        }}
        {...rest}
      />

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: error || helperText || maxLength ? 18 : 0,
        }}>
        {/* Error / helper */}
        {error ? (
          <FormHelperText error sx={{ m: 0 }}>
            {error}
          </FormHelperText>
        ) : helperText ? (
          <FormHelperText sx={{ m: 0 }}>{helperText}</FormHelperText>
        ) : (
          <span />
        )}

        {/* Character counter */}
        {maxLength && (
          <Typography
            variant="caption"
            sx={{
              color: atLimit
                ? 'error.main'
                : nearLimit
                  ? 'warning.main'
                  : 'text.disabled',
              fontVariantNumeric: 'tabular-nums',
            }}>
            {charCount}/{maxLength}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CustomInputField;
